<template>
  <div class="bg-white p-4 lg:p-8 mb-4 md:mb-0 flex">
    <div class="w-full">
      <div class="flex flex-col h-full lg:ml-24">
        <div class="my-auto w-full flex flex-col h-full">
          <div class="pt-4 pr-4 pb-4 flex justify-start">
            <span class="flex items-center">
              <img
                class="h-8 w-auto sm:h-7 logo"
                src="/images/black_logo.png"
                alt="Financial Cents Logo"
              />
            </span>
          </div>
          <div class="flex flex-col grow">
            <div class="my-auto">
              <div class="text-left">
                <h1 class="text-left text-3xl font-bold mb-1 text-blue-900">Tell us about your firm</h1>
                <p class="mb-4 md:mb-8 text-base text-gray-500">
                  We'll use these details to customize your experience.
                </p>
              </div>
              <form class="bg-white rounded registration">
                <div class="mb-6">
                  <v-label
                    class="block mb-1"
                    title="Firm name"
                  >
                    <template #content>
                      <span class="text-green-600">*</span>
                    </template>
                  </v-label>
                  <on-boarding-input
                    input-type="text"
                    name="company_name"
                    autocomplete="company-name"
                    v-model="form.company_name"
                    class="w-full rounded-lg"
                    placeholder-text="Firm name"
                  />
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('company_name')"
                  >
                    {{ form.errors.first("company_name") }}
                  </p>
                </div>
                <div class="mb-6">
                  <v-label
                    class="block mb-1"
                    title="Phone number"
                  >
                    <template #content>
                      <span class="text-green-600">*</span>
                    </template>
                  </v-label>
                  <div class="flex gap-2">
                    <select
                      v-model="form.country_code"
                      class="border border-gray-300 rounded-lg text-sm text-gray-500 p-3 focus:outline-none focus:border-green-500 active:border-green-500 w-28 shrink-0"
                    >
                      <option
                        v-for="cc in countryCodes"
                        :key="cc.short_name"
                        :value="cc.code"
                      >
                        {{ cc.short_name }} {{ cc.code }}
                      </option>
                    </select>
                    <input
                      v-maska="'###-###-####'"
                      type="text"
                      name="phone"
                      v-model="form.phone"
                      autocomplete="phone"
                      placeholder="000-000-0000"
                      class="border border-gray-300 rounded-lg p-3 hover:border-green-600 text-sm text-gray-500 focus:outline-none focus:border-green-500 active:border-green-500 w-full"
                    />
                  </div>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('phone')"
                  >
                    {{ form.errors.first("phone") }}
                  </p>
                </div>
                <div class="mb-6">
                  <v-label
                    class="block mb-1"
                    title="Firm size (including you)"
                  >
                    <template #content>
                      <span class="text-green-600">*</span>
                    </template>
                  </v-label>
                  <div
                    class="relative inline-block align-middle w-full btn-group hover:border-green-600 cursor-pointer z-10"
                  >
                    <select
                      v-model="form.firm_size"
                      class="border border-gray-300 rounded-lg text-sm text-gray-500 dropdown-toggle w-full p-3 flex focus:outline-none focus:border-green-500 active:border-green-500"
                    >
                      <option value="">Please select one</option>
                      <option
                        v-for="(firmSize, index) in firmSizeObject"
                        :key="'firmSize_' + index"
                      >
                        {{ firmSize }}
                      </option>
                    </select>
                  </div>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('firm_size')"
                  >
                    {{ form.errors.first("firm_size") }}
                  </p>
                </div>
                <div class="mb-6">
                  <v-label
                    class="block mb-1"
                    title="What is your primary service provided to clients?"
                  >
                    <template #content>
                      <span class="text-green-600">*</span>
                    </template>
                  </v-label>
                  <div
                    class="relative inline-block align-middle w-full btn-group hover:border-green-600 cursor-pointer z-10"
                  >
                    <select
                      v-model="form.primary_service"
                      class="border border-gray-300 rounded-lg text-sm text-gray-500 dropdown-toggle w-full p-3 flex focus:outline-none focus:border-green-500 active:border-green-500"
                    >
                      <option value="">Please select one</option>
                      <option
                        v-for="service in primaryServiceOptions"
                        :key="service.value"
                        :value="service.value"
                      >
                        {{ service.label }}
                      </option>
                    </select>
                  </div>
                  <p
                    class="help is-danger text-xs"
                    v-if="form.errors.has('primary_service')"
                  >
                    {{ form.errors.first("primary_service") }}
                  </p>
                </div>
                <div class="h-[1px] my-8 bg-gray-300"></div>
                <div class="mb-[45px]">
                  <v-button
                    :loading="loading"
                    :disabled="loading"
                    type="button"
                    @click.prevent="submit"
                    class="w-full mt-4 sm:mt-8 rounded-lg h-12 px-4 py-2 text-white bg-green-600 hover:bg-green-500 font-light"
                  >
                    <template #title>
                      <span v-if="!loading && !submitting">Complete sign up</span>
                    </template>
                  </v-button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { vMaska } from "maska/vue"
import OnBoardingInput from "../form/OnBoardingInput.vue"
import Form from "../../../helpers/Form"
import VButton from "../../../../ClientPortal/components/VButton.vue"
import VLabel from "../form/VLabel.vue"
import { FIRM_SIZES, PRIMARY_SERVICE_OPTIONS, fetchCountryCodes } from "../../../constants/firmInfo"

export default {
  name: "FirmInfo",

  components: {
    OnBoardingInput,
    VButton,
    VLabel,
  },

  directives: {
    maska: vMaska,
  },

  emits: ["set-registration-detail", "submit", "update:company-name"],

  props: {
    initialCompanyName: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      loading: false,
      countryCodes: [],
      form: new Form({
        company_name: this.initialCompanyName,
        phone: "",
        country_code: "+1",
        firm_size: "",
        primary_service: "",
      }),
      firmSizeObject: FIRM_SIZES,
      primaryServiceOptions: PRIMARY_SERVICE_OPTIONS,
    }
  },

  watch: {
    initialCompanyName(value) {
      if (value && !this.form.company_name) {
        this.form.company_name = value
      }
    },
    "form.company_name": {
      handler(value) {
        this.$emit("update:company-name", value)
      },
      immediate: true,
    },
  },

  async mounted() {
    this.countryCodes = await fetchCountryCodes()
  },

  methods: {
    async submit() {
      if (this.loading) return
      try {
        this.loading = true
        const phoneDigits = String(this.form.phone || "").replace(/\D/g, "")
        await this.form.post(`/api/register/firm-info`, {
          phone: phoneDigits,
        })
        this.form.errors.clearAll()
        this.$emit("set-registration-detail", this.form.data({}))
        this.$emit("submit")
      } catch (e) {
        console.error(e)
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
h1 {
  font-family: "Sora", sans-serif;
}
</style>
